import type { TFunction } from 'i18next';

/** Cijeli broj sa razmakom kao separatorom hiljada (kao u dizajnu): 1245 -> "1 245". */
export function fmtInt(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const s = Math.round(Math.abs(n))
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return n < 0 ? `−${s}` : s;
}

/** Decimalni broj sa zarezom: 4.2 -> "4,2". */
export function fmtDec(n: number | null | undefined, digits = 1): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return n.toFixed(digits).replace('.', ',');
}

/** "18 420 KM" */
export function fmtMoney(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${fmtInt(n)} KM`;
}

/** "1 245 × 1 478" ili "—" */
export function fmtSize(w: number | null | undefined, h: number | null | undefined): string {
  if (!w || !h) return '—';
  return `${fmtInt(w)} × ${fmtInt(h)}`;
}

const pad = (n: number) => String(n).padStart(2, '0');

function toDate(v: string | Date): Date {
  return typeof v === 'string' ? new Date(v) : v;
}

/** "14.10." iz `YYYY-MM-DD` ili ISO vremena. */
export function fmtDayMonth(v: string | null | undefined): string {
  if (!v) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (m) return `${m[3]}.${m[2]}.`;
  const d = toDate(v);
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.`;
}

/** "14.10.2026." */
export function fmtDate(v: string | null | undefined): string {
  if (!v) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (m) return `${m[3]}.${m[2]}.${m[1]}.`;
  const d = toDate(v);
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}.`;
}

/** "09:11" */
export function fmtTime(v: string | Date): string {
  const d = toDate(v);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "09.10. 09:11" */
export function fmtDayMonthTime(v: string | null | undefined): string {
  if (!v) return '—';
  return `${fmtDayMonth(v)} ${fmtTime(v)}`;
}

/** "Danas 09:40", "Juče 16:04", "08.10. 11:22". */
export function fmtRelativeDay(v: string, t: TFunction, now: Date = new Date()): string {
  const d = toDate(v);
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(now) - startOf(d)) / 86_400_000);
  if (diffDays === 0) return t('panel:time.today', { time: fmtTime(d) });
  if (diffDays === 1) return t('panel:time.yesterday', { time: fmtTime(d) });
  return fmtDayMonthTime(v);
}

/** "Prije 12 min", "Prije 2 h", "Danas 08:15", "Juče 16:30". */
export function fmtAgo(v: string, t: TFunction, now: Date = new Date()): string {
  const d = toDate(v);
  const mins = Math.floor((now.getTime() - d.getTime()) / 60_000);
  if (mins < 1) return t('panel:time.justNow');
  if (mins < 60) return t('panel:time.minutesAgo', { count: mins });
  if (mins < 180) return t('panel:time.hoursAgo', { count: Math.floor(mins / 60) });
  return fmtRelativeDay(v, t, now);
}

/** Inicijali iz imena: "Milena Jovanović" -> "MJ". */
export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

/** "Nemanja Đurić" -> "Nemanja". */
export function firstName(name: string | null | undefined): string {
  return name?.split(' ')[0] ?? '—';
}
